import http from './http'

/**
 * The body of the server's response.
 * @typedef {Object} ServerResponse
 * @property {boolean} error=false
 * @property {(Object|string)} message
 * @property {!number} status
 */

const {
  NODE_ENV,
  VUE_APP_SERVER_HOST_LOCAL,
  VUE_APP_SERVER_HOST_REMOTE,
  VUE_APP_SERVER_TARGET,
  VUE_APP_SERVER_FUNCTION_NAME,
} = process.env

const devEnvs = ['development', 'test']

const getServerHost = () => {
  const target = typeof VUE_APP_SERVER_TARGET === 'string' ? VUE_APP_SERVER_TARGET.toLowerCase() : 'auto'

  if (target === 'local' && VUE_APP_SERVER_HOST_LOCAL) {
    return VUE_APP_SERVER_HOST_LOCAL
  }

  if (target === 'remote' && VUE_APP_SERVER_HOST_REMOTE) {
    return VUE_APP_SERVER_HOST_REMOTE
  }

  return (devEnvs.indexOf(NODE_ENV) !== -1 && VUE_APP_SERVER_HOST_LOCAL) || VUE_APP_SERVER_HOST_REMOTE
}

const SERVER_HOST = getServerHost()

const FIREBASE_FUNCTION_NAME = VUE_APP_SERVER_FUNCTION_NAME || 'server'

const shouldAppendFunctionName = (host) => {
  if (!host || typeof host !== 'string') {
    return false
  }

  // Function name is required for direct Cloud Functions hosts,
  // but should be omitted for Firebase Hosting rewrite domains.
  return host.includes('cloudfunctions.net') || host.includes('localhost:5001')
}

const getServerBaseUrl = () => {
  if (shouldAppendFunctionName(SERVER_HOST)) {
    return `${SERVER_HOST}/${FIREBASE_FUNCTION_NAME}`
  }

  return SERVER_HOST
}

const server = {
  host: getServerBaseUrl(),
  paths: {
    base: () => '/',
    admin: (path, queryString) => `/admin/${path}${queryString && queryString.length > 0 ? `?${queryString}` : ''}`,
    decode: (tokenType) => `/decode/${tokenType}`,
    feedback: () => '/feedback',
    generate: (tokenType) => `/generate/${tokenType}`,
    health: () => '/health',
    verify: (tokenType) => `/verify/${tokenType}`,
  },
  /**
   * Sends a GET request to server endpoints.
   * @param {string} path A path on the application server, e.g. /generate.
   * @param {Object=} additionalHeaders
   * @returns {Promise<ServerResponse>}
   */
  get(path, additionalHeaders) {
    const config = http.config.get(true, additionalHeaders)
    return http.get(`${this.host}${path}`, config)
  },
  /**
   * Sends a POST request to server endpoints.
   * @param {string} path A path on the application server, e.g. /generate
   * @param {Object=} data
   * @param {Object=} additionalHeaders
   * @returns {Promise<ServerResponse>}
   */
  post(path, data = {}, additionalHeaders = {}) {
    const config = http.config.get(true, additionalHeaders)
    return http.post(`${this.host}${path}`, data, config)
  },
  /**
   * Makes a request to the server's health endpoint.
   * @returns {Promise<ServerResponse>}
   */
  health() {
    return this.get(this.paths.health())
  },
  /**
   * Prompts the server to generate a token and return it or an error object.
   * @returns {Promise<ServerResponse>}
   */
  generate(tokenType) {
    return this.post(this.paths.generate(tokenType))
  },

  /**
   * Submits user feedback to the server endpoint.
   * @param {Object} payload
   * @returns {Promise<ServerResponse>}
   */
  submitFeedback(payload) {
    return this.post(this.paths.feedback(), payload)
  },

  listFeedbackEntries({ status = 'all', limit = 200 } = {}) {
    const path = 'feedback/list'
    const queryString = `status=${encodeURIComponent(status)}&limit=${encodeURIComponent(limit)}`

    return this.adminGet(path, queryString)
  },

  updateFeedbackEntry(feedbackId, data = {}) {
    const path = 'feedback/update'
    const queryString = `feedbackId=${encodeURIComponent(feedbackId || '')}`

    return this.adminPost(path, queryString, { data })
  },

  getFeedbackNotificationConfig() {
    return this.adminGet('feedback/config', '')
  },

  updateFeedbackNotificationConfig(data = {}) {
    return this.adminPost('feedback/updateConfig', '', { data })
  },
  /**
   * Decodes the token if it is valid, or returns an error object if it's invalid.
   * @param {object} additionalHeaders For Tableau tokens, set the Tableau header with the token to decode.
   * @returns {Promise<ServerResponse>}
   */
  decode(tokenType, additionalHeaders) {
    return this.get(this.paths.decode(tokenType), additionalHeaders)
  },
  /**
   * Verifies the token if it is valid, or returns an error object if it's invalid.
   * @param {object} additionalHeaders For Tableau tokens, set the Tableau header with the token to verify.
   * @returns {Promise<ServerResponse>}
   */
  verify(tokenType, additionalHeaders) {
    return this.get(this.paths.verify(tokenType), additionalHeaders)
  },

  /**
   * Prompts the server to run an admin action and return the result or an error object.
   * @param {string} path The object and action to reference on the server.
   * @param {string} queryString The query to filter the results.
   * @param {object} additionalHeaders Set any headers needed for the request.
   * @returns {Promise<ServerResponse>}
   */
  adminGet(path, queryString, additionalHeaders = {}) {
    return this.get(this.paths.admin(path, queryString), additionalHeaders)
  },

  adminPost(path, queryString, data = {}, additionalHeaders = {}) {
    return this.post(this.paths.admin(path, queryString), data, additionalHeaders)
  },

  getTenant(tenantId) {
    const path = 'tenant/get'
    const querystring = `tenantId=${tenantId}`

    return this.adminGet(path, querystring)
  },

  listDomains() {
    const path = 'domains/list'
    const queryString = ''

    return this.adminGet(path, queryString)
  },

  listTenants() {
    const path = 'tenants/list'
    const queryString = ''

    return this.adminGet(path, queryString)
  },

  updateTenant(tenantId, data) {
    const path = `tenant/update/${tenantId}`
    const queryString = ''

    return this.adminPost(path, queryString, { data })
  },

  getUser(tenantId, uid) {
    const path = 'user/get'
    const queryString = `tenantId=${tenantId}&uid=${uid}`

    return this.adminGet(path, queryString)
  },

  getUsers(tenantId) {
    const path = 'users/list'
    const queryString = `tenantId=${tenantId}`

    return this.adminGet(path, queryString)
  },

  registerUser(tenantId, data) {
    const path = 'user/register'
    const queryString = `tenantId=${tenantId}`

    return this.adminPost(path, queryString, { data })
  },

  setCustomClaims(tenantId, data) {
    const path = 'claims/set'
    const queryString = `tenantId=${tenantId}`

    return this.adminPost(path, queryString, { data })
  },

  getCustomClaims(tenantId, uid) {
    const path = 'claims/get'
    const queryString = `tenantId=${tenantId}&uid=${uid}`

    return this.adminGet(path, queryString)
  },

  deleteCustomClaims(tenantId, uid) {
    const path = 'claims/delete'
    const queryString = `tenantId=${tenantId}&uid=${uid}`

    return this.adminGet(path, queryString)
  },

  getDomainByTenantId(tenantId) {
    const path = 'domain/get'
    const queryString = `tenantId=${tenantId}`

    return this.adminGet(path, queryString)
  },

  createProvider(domainName, providerId, data) {
    const path = 'provider/create'
    const queryString = `domainName=${domainName}&providerId=${providerId}`

    return this.adminPost(path, queryString, { data })
  },

  listProviders(domainName, providerType) {
    const path = 'providers/list'
    const queryString = `domainName=${domainName}&providerType=${providerType}`

    return this.adminGet(path, queryString)
  },

  updateProvider(domainName, providerId, data) {
    const path = 'provider/update'
    const queryString = `domainName=${domainName}&providerId=${providerId}`

    return this.adminPost(path, queryString, { data })
  },

  disableProvider(domainName, providerId, data) {
    const path = 'provider/disable'
    const queryString = `domainName=${domainName}&providerId=${providerId}`

    return this.adminPost(path, queryString, { data })
  },

  enableProvider(domainName, providerId, data) {
    const path = 'provider/enable'
    const queryString = `domainName=${domainName}&providerId=${providerId}`

    return this.adminPost(path, queryString, { data })
  },
}

export default server
